import { IGetSpendingsResponse } from 'multirpc-sdk';

import { convertCreditsToUSD } from 'modules/billing/utils/convertCreditsToUSD';
import { CREDITS_TO_REQUESTS_RATE } from 'modules/billing/constants';
import { DEAL_PROMO_EXTRA_REQUESTS_RATE } from 'modules/payments/components/PaymentForm/components/AmountInput/const';

const MINIMUM_COUNT_FOR_AVERAGE = 2;
const DECIMAL_PLACES = 3;

export interface SpendingAmounts {
  bundles: number;
  payg: number;
}

export interface TotalSpending {
  credits: number;
  usd: number;
  requests: number;
}

/**
 * Calculates total spending credits from spending data
 * @param spendingData - Raw spending data from API
 * @returns Object with bundles and payg totals, or undefined if no data
 */
export const calculateTotalSpendingCredits = (
  spendingData?: IGetSpendingsResponse,
): SpendingAmounts | undefined => {
  if (!spendingData?.stats) return undefined;

  return spendingData.stats.reduce(
    (acc, curr) => {
      acc.bundles += Number(curr.stats?.bundles.total.credit_amount) || 0;
      acc.payg += Number(curr.stats?.payg) || 0;

      return acc;
    },
    { bundles: 0, payg: 0 },
  );
};

/**
 * Counts spending items that have stats data
 * @param spendingData - Raw spending data from API
 * @returns Count of items with stats, or 0 if no data
 */
export const getSpendingItemsCount = (
  spendingData?: IGetSpendingsResponse,
): number => {
  if (!spendingData) return 0;

  return spendingData.stats.filter(statItem => Boolean(statItem.stats)).length;
};

/**
 * Determines if average should be hidden based on item count
 * @param count - Number of spending items
 * @returns true if average should be hidden (less than minimum count)
 */
export const isAverageHidden = (count: number): boolean => {
  return count < MINIMUM_COUNT_FOR_AVERAGE;
};

/**
 * Calculates average spending in credits
 * @param totalCreditsSpending - Total spending amounts
 * @param count - Number of items to average over
 * @returns Average spending amounts, or undefined if calculation not possible
 */
export const calculateAverageSpendingCredits = (
  totalCreditsSpending: SpendingAmounts | undefined,
  count: number,
): SpendingAmounts | undefined => {
  if (!totalCreditsSpending || count === 0) return undefined;

  return {
    bundles: totalCreditsSpending.bundles / count,
    payg: totalCreditsSpending.payg / count,
  };
};

/**
 * Converts average spending credits to USD
 * @param averageSpendingCredits - Average spending in credits
 * @returns Average spending in USD, returns zeros if no data
 */
export const calculateAverageSpendingUsd = (
  averageSpendingCredits?: SpendingAmounts,
): SpendingAmounts => {
  if (!averageSpendingCredits) {
    return { bundles: 0, payg: 0 };
  }

  return {
    bundles: convertCreditsToUSD(
      averageSpendingCredits.bundles,
      DECIMAL_PLACES,
      DEAL_PROMO_EXTRA_REQUESTS_RATE,
    ).toNumber(),
    payg: convertCreditsToUSD(
      averageSpendingCredits.payg,
      DECIMAL_PLACES,
    ).toNumber(),
  };
};

/**
 * Calculates total spending in credits, USD, and requests
 * @param totalCreditsSpending - Total spending amounts
 * @returns Total spending data, or undefined if no data
 */
export const calculateTotalSpending = (
  totalCreditsSpending?: SpendingAmounts,
): TotalSpending | undefined => {
  if (!totalCreditsSpending) return undefined;

  const totalCredits = totalCreditsSpending.bundles + totalCreditsSpending.payg;

  const totalPaygUsd = convertCreditsToUSD(
    totalCreditsSpending.payg,
    DECIMAL_PLACES,
  ).toNumber();

  const totalBundlesUsd = convertCreditsToUSD(
    totalCreditsSpending.bundles,
    DECIMAL_PLACES,
    DEAL_PROMO_EXTRA_REQUESTS_RATE,
  ).toNumber();

  return {
    credits: totalCredits,
    usd: totalPaygUsd + totalBundlesUsd,
    requests: Math.floor(totalCredits / CREDITS_TO_REQUESTS_RATE),
  };
};
